/**
 * The room's cards as Walk it through reads them, shared by the computer's walk and the phone's and iPad's: the
 * moments in order, the offer, the words a moment is said in, and a file as Assets names it.
 */
import { useEffect } from "react";
import { BOARD_CAPS } from "../../../shared/board-caps";
import type { BoardCard, BoardCards, BoardState, LibraryAsset } from "../../../shared/types";
import { bytesLabel, kindName, lenLabel } from "../../assets/library";
import type { IconName } from "../../components/ui/Icon";
import { loadLibrary, useLibrary } from "../../assets/store";
import { OFFER_FOLD_MS, offerNeedsLink } from "../../../shared/offer";
import type { BoardHost } from "../host";
import { chapterRowsOf } from "../make/MakeChapters";

export type Moment = BoardCards["poll"] | BoardCards["question"] | BoardCards["reaction"] | BoardCards["handout"];
export type Offer = BoardCards["offer"];

export const MOMENT_KINDS = new Set<BoardCard["kind"]>(["poll", "question", "reaction", "handout"]);
/** A second as the producer is asked for it: minutes and seconds, past an hour too ("72:05"). */
export const minuteMark = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.round(sec) % 60).padStart(2, "0")}`;
export const KIND: Record<Moment["kind"], { name: string; icon: IconName; held: string; tag: string }> = {
  poll: { name: "Poll", icon: "bars", held: "paused for your answer", tag: "A quick poll" },
  question: { name: "Quick question", icon: "question", held: "paused for your answer", tag: "A quick question" },
  reaction: { name: "Reaction", icon: "heart", held: "paused so you can react", tag: "A moment to react" },
  handout: { name: "Handout", icon: "file", held: "paused to hand you this", tag: "A handout" },
};
/** The button a new offer starts with; its words are the host's to write. */
export const NEW_BTN: Record<Offer["offerType"], string> = { call: "Book a call", buy: "Buy now", code: "", download: "Download it", message: "" };
/** How long a moment stays on screen in the preview. */
export const MOMENT_SEC = 45;

export function momentsOf(state: BoardState): Moment[] {
  return state.room.filter((c): c is Moment => MOMENT_KINDS.has(c.kind)).toSorted((a, b) => (a.t ?? 0) - (b.t ?? 0));
}
export function offerOf(state: BoardState): Offer | null {
  return (state.room.find((c) => c.kind === "offer") as Offer | undefined) ?? null;
}
export function momentWords(m: Moment): string {
  return m.kind === "reaction" ? m.prompt : m.kind === "handout" ? m.name : m.q;
}
/** `fileName` is the picked file's own name; the stored key's tail is only for a file Assets no longer lists. */
export function offerLabel(o: Offer, fileName?: string): string {
  if (o.offerType === "code") return o.code ? `The code ${o.code}` : "A code, not set yet";
  if (o.offerType === "download") return o.file ? `A download: ${fileName ?? o.file.split("/").pop()}` : "A download, no file yet";
  if (o.offerType === "message") return "A message, no button";
  if (o.offerType === "buy") return `A checkout button · ${o.btn}`;
  return `A booking button · ${o.btn}`;
}

/** What the offer's row and pin say: "No link yet" while Publish would refuse it for a missing link, else what it is. */
export function offerWords(o: Offer, fileName?: string): string {
  return o.on && offerNeedsLink(o) ? "No link yet" : offerLabel(o, fileName);
}

/** A link as a guest reads it under a code: "mayatorres.com/book". */
export function bareUrl(url: string): string {
  return url.trim().replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "");
}

/** A file picked in Assets, as the room shows it: its name, the small line under it, and its picture. */
export function fileFace(assets: readonly LibraryAsset[], key: string | undefined, note?: string): { name: string; sub: string; pic: string | null } | null {
  const a = assets.find((x) => (key && x.key === key) || (note && x.note_id === note));
  if (!a) return null;
  const sub = [
    kindName(a),
    a.pages ? `${a.pages} page${a.pages === 1 ? "" : "s"}` : "",
    a.duration_sec != null ? lenLabel(a.duration_sec) : "",
    a.size_bytes ? bytesLabel(a.size_bytes) : "",
  ].filter(Boolean).join(" · ");
  return { name: a.name, sub, pic: a.kind === "image" ? a.url : a.poster_url };
}

/** A handout's or the offer's file: its picture when Assets has one, else a page drawn small. */
export function fileThumb(pic: string | null | undefined) {
  return pic ? (
    <span className="fl-athumb">
      <img src={pic} alt="" />
    </span>
  ) : (
    <span className="bw-mdoc">
      <i />
      <i />
      <i />
    </span>
  );
}

/** The field a moment's words live in, and the most it keeps. */
export const WORDS: Record<Moment["kind"], { field: "q" | "prompt" | "name"; max: number }> = {
  poll: { field: "q", max: BOARD_CAPS.line },
  question: { field: "q", max: BOARD_CAPS.line },
  reaction: { field: "prompt", max: BOARD_CAPS.line },
  handout: { field: "name", max: BOARD_CAPS.handoutName },
};

/** A pin is never dragged closer than this to the start, nor to the end. */
export const PIN_START = 5;
export const PIN_END = 15;

/** Where a new moment could go: the middle of every long stretch with nothing in it. */
export function gapsIn(marks: readonly number[], dur: number): number[] {
  const out: number[] = [];
  let prev = 0;
  for (const t of marks.toSorted((a, b) => a - b).concat([dur])) {
    if (t - prev >= 170) out.push(Math.round((prev + t) / 2 / 10) * 10);
    prev = t;
  }
  return out;
}


/** The files on the room's cards are named and sized as Assets has them. */
export function useRoomFiles(state: BoardState) {
  const lib = useLibrary();
  const wantsFiles = state.room.some((c) => (c.kind === "handout" && !!c.key) || (c.kind === "offer" && !!(c.file || c.videoNote)));
  useEffect(() => {
    if (wantsFiles) void loadLibrary();
  }, [wantsFiles]);
  return lib;
}

/** How long the room runs, in seconds: the talk's, else the live's, else a little past the last chapter. */
export function roomDuration(state: BoardState, host: BoardHost): number {
  const live = host.server.scheduleMode === "live";
  const last = chapterRowsOf(state).at(-1)?.t ?? 0;
  return Math.max(60, host.duration || (live ? (host.server.live?.lengthMin ?? 60) * 60 : last + 120));
}

/**
 * What is up on the room at second `at`: the moment, whether the offer is under the talk, and whether it is open. As in
 * the guest's room, the offer lands open at its second, folds to its tab when a moment comes up, and stays a tab after.
 */
export function onScreen(state: BoardState, at: number, live: boolean): { up: Moment | undefined; showOffer: boolean; offerOpen: boolean } {
  const offer = offerOf(state);
  const up = momentsOf(state).find((m) => m.on && at >= (m.t ?? 0) && at < (m.t ?? 0) + MOMENT_SEC);
  // The offer is up from its second, to the end or for its window, as the room shows it.
  const showOffer = !!offer && at >= (offer.t ?? 0) && (typeof offer.showFor !== "number" || at < (offer.t ?? 0) + offer.showFor);
  const offerOpen = showOffer && !up && at < (offer?.t ?? 0) + (live ? OFFER_FOLD_MS.live : OFFER_FOLD_MS.room) / 1000;
  return { up, showOffer, offerOpen };
}
